import React, { useMemo } from 'react';
import { isEmpty, size } from 'lodash';
import { useSelector } from 'react-redux';
import styled from 'styled-components/macro';
import i18n from 'i18n';
import useVodSearch from 'hooks/data/useVodSearch';
import CustomCollection from 'components/CustomCollection';
import Clock from 'components/Clock';
import scaleSize from 'utils/scale-size';
import Grid, { GridListsWrapper, GridTitle } from 'components/Grid';
import ListItem from 'components/ListItem';
import { LIST_ITEMS_SIZE } from 'config';
import { selectIsMenuVisible } from 'reducers/menu/selectors';
import { LoaderComponent } from 'components/Loader';
import useLoadDefaultContent from './useLoadDefaultContent';
import { StyledBackdrop } from './styled';

const StyledContentWrapper = styled.div`
  position: relative;
  height: 100%;
  width: ${({ $width }) => scaleSize($width ?? 0)}px;
  left: ${({ $left }) => scaleSize($left ?? 0)}px;
  margin-left: ${scaleSize(50)}px;
  overflow: hidden;
`;

const StyledGrid = styled(Grid)`
  width: ${scaleSize(1700)}px;

  ${GridTitle} {
    top: ${scaleSize(140)}px;
    font-weight: 500;
    font-size: ${scaleSize(40)}px;
    line-height: 1.2;
    color: ${({ theme }) => theme.defaultColor};
    font-family: RobotoMedium, sans-serif;
  }

  ${GridListsWrapper} {
    padding-top: ${scaleSize(220)}px;
  }
`;

const StyledNothingFound = styled.div`
  padding-top: ${scaleSize(160)}px;
  font-size: ${scaleSize(35)}px;
  text-align: center;
  margin-left: -50px;
`;

const addWatchMore = (arr, contentType) => {
  const item = {
    type: 'VIEW_ALL',
    title: i18n.t('common.watchAllButton'),
    titles: {
      UA: i18n.t('common.watchAllButton'),
      EN: i18n.t('common.watchAllButton'),
    },
    contentType,
  };

  return [...arr.slice(0, 20), item];
};

const ContentPanel = ({ showMovies, showSeries, search, onItemFocus, onItemEnter, isFiltersFolded }) => {
  const isMenuVisible = useSelector(selectIsMenuVisible);
  const contentViewWidth = isFiltersFolded ? (isMenuVisible ? 1710 : 1810) : 1230;
  const someFiltersSelected = !isEmpty(search);
  const isCollectionView = showMovies && showSeries && someFiltersSelected;

  const { defaultGrid, recommendations, isLoading: isDefaultContentLoading } = useLoadDefaultContent();

  const defaultCollection = useMemo(
    () => ({
      content: defaultGrid,
      id: 'pre-defined',
    }),
    [defaultGrid],
  );

  const baseLeftOffset = useMemo(() => {
    if (isFiltersFolded) {
      if (isMenuVisible) return 70;
      return 0;
    }

    return 580;
  }, [isFiltersFolded, isMenuVisible]);

  const {
    data: movieData,
    isLoading: isMoviesLoading,
    hasNextPage: isMoviesHasNextPage,
    fetchNextPage: fetchNextMoviePage,
    isFetchingNextPage: isMoviesFetchingNextPage,
  } = useVodSearch(
    {
      type: 'VOD_ASSET',
      'f_metadata.contentType': 'MOVIE',
      ...search,
    },
    { enabled: showMovies && someFiltersSelected, keepPreviousData: false },
  );

  const {
    data: seriesData,
    isLoading: isSeriesLoading,
    hasNextPage: isSeriesHasNextPage,
    fetchNextPage: fetchNextSeriesPage,
    isFetchingNextPage: isSeriesFetchingNextPage,
  } = useVodSearch(
    {
      type: 'TV_SERIES',
      'f_metadata.contentType': 'not(EPISODE)',
      ...search,
    },
    { enabled: showSeries && someFiltersSelected, keepPreviousData: false },
  );

  const isLoading = isMoviesLoading || isSeriesLoading || isDefaultContentLoading;
  const isOnLazyload = isMoviesFetchingNextPage || isSeriesFetchingNextPage;

  const collections = useMemo(() => {
    const result = [];

    if (!isEmpty(movieData)) {
      result.push({
        content: isMoviesHasNextPage && isCollectionView ? addWatchMore(movieData, 'VOD_ASSET') : movieData,
        contentSize: 'LARGE',
        title: i18n.t('filtersPage.contentTypes.films'),
        id: 'movies',
        fetchNextPage: fetchNextMoviePage,
      });
    }

    if (!isEmpty(seriesData)) {
      result.push({
        content: isSeriesHasNextPage && isCollectionView ? addWatchMore(seriesData, 'TV_SERIES') : seriesData,
        contentSize: 'LARGE',
        title: i18n.t('filtersPage.contentTypes.series'),
        id: 'series',
        fetchNextPage: fetchNextSeriesPage,
      });
    }

    return result;
  }, [
    movieData,
    seriesData,
    isMoviesHasNextPage,
    isCollectionView,
    fetchNextMoviePage,
    isSeriesHasNextPage,
    fetchNextSeriesPage,
  ]);

  const content = useMemo(() => {
    if (isLoading) return null;

    if (!someFiltersSelected) {
      return (
        <StyledGrid
          // Explicitly renders diffrent grid than content based grid
          // It fixes focus position related bug
          key={defaultCollection.id}
          gridId={defaultCollection.id}
          items={defaultCollection.content}
          rowSize={someFiltersSelected ? 4 : 3}
          component={ListItem}
          contentSize={'FILTERS_BANNER'}
          itemSize={LIST_ITEMS_SIZE.FILTERS_BANNER}
          onFocus={onItemFocus}
          onEnter={onItemEnter}
          defaultSection={isFiltersFolded}
        />
      );
    }

    if (isEmpty(collections)) {
      const hasRecommendations = !isEmpty(recommendations?.content);
      return (
        <>
          <StyledNothingFound>
            <p>{i18n.t('filtersPage.nothingFound')}</p>
            {hasRecommendations && <p>{i18n.t('filtersPage.tryDifferentFilers')}</p>}
          </StyledNothingFound>
          {hasRecommendations && (
            <CustomCollection
              collections={[recommendations]}
              startListScrollIndex={1}
              onContentEnter={onItemEnter}
              onContentFocus={onItemFocus}
              disableDefaultFocus={!isFiltersFolded}
            />
          )}
        </>
      );
    }

    return isCollectionView ? (
      <CustomCollection
        top={scaleSize(100)}
        collections={collections}
        startListScrollIndex={1}
        onContentEnter={onItemEnter}
        onContentFocus={onItemFocus}
        disableDefaultFocus={!isFiltersFolded}
      />
    ) : (
      <StyledGrid
        gridId={'single-type'}
        title={collections[0].title}
        items={collections[0].content}
        rowSize={someFiltersSelected ? 4 : 3}
        component={ListItem}
        contentSize={'LARGE'}
        itemSize={LIST_ITEMS_SIZE.LARGE}
        onFocus={(_, index) => {
          const collection = collections[0];
          const shouldLoadMore = size(collection.content) - 5 <= index;
          if (shouldLoadMore) collection?.fetchNextPage();
          onItemFocus();
        }}
        onEnter={onItemEnter}
        defaultSection={isFiltersFolded}
      />
    );
  }, [
    isLoading,
    collections,
    someFiltersSelected,
    isCollectionView,
    onItemEnter,
    onItemFocus,
    isFiltersFolded,
    recommendations,
    defaultCollection,
  ]);

  return (
    <>
      <StyledContentWrapper $left={baseLeftOffset} $width={contentViewWidth}>
        <Clock noAnimation />
        {isLoading ? null : content}
        {(isLoading || isOnLazyload) && <LoaderComponent />}
      </StyledContentWrapper>
      {isOnLazyload && <StyledBackdrop />}
    </>
  );
};

export default ContentPanel;
